// Admin notifications hub (item 18 — IA cleanup). Consolidates the
// three previously-separate admin pages —
//   • Recipients & rules        (AdminNotifications)
//   • Channels                  (AdminNotificationChannels)
//   • Webhook subscriptions     (AdminWebhooks)
// — into one tabbed surface. The wire-format paths under
// /api/admin/{notifications,notification-channels,webhooks} are
// unchanged; this is purely a frontend IA refactor so operators
// configuring "how the platform talks to the outside world" land
// on a single page.
//
// Tab state lives in the `?tab=` query param so a deep link is
// shareable and the browser back-button works between tabs. Old
// routes (/admin/notification-channels, /admin/webhooks) redirect
// here with the right tab pre-selected (see App.tsx).

import { useSearchParams } from 'react-router-dom';
import { Mail, MessageSquare, Webhook, type LucideIcon } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { AdminNotificationsBody } from './AdminNotifications';
import { AdminNotificationChannelsBody } from './AdminNotificationChannels';
import { AdminWebhooksBody } from './AdminWebhooks';

type TabKey = 'recipients' | 'channels' | 'webhooks';

interface TabSpec {
  key: TabKey;
  label: string;
  Icon: LucideIcon;
}

const TABS: TabSpec[] = [
  { key: 'recipients', label: 'Recipients & rules', Icon: Mail },
  { key: 'channels', label: 'Channels', Icon: MessageSquare },
  { key: 'webhooks', label: 'Webhook subscriptions', Icon: Webhook },
];

function isTabKey(v: string | null): v is TabKey {
  return v === 'recipients' || v === 'channels' || v === 'webhooks';
}

export function AdminNotificationsHub() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const active: TabKey = isTabKey(raw) ? raw : 'recipients';

  const setActive = (k: TabKey) => {
    // 'recipients' is the default — drop the param to keep the URL
    // clean when landing on it from a redirect.
    if (k === 'recipients') {
      setParams({}, { replace: false });
    } else {
      setParams({ tab: k }, { replace: false });
    }
  };

  return (
    <AdminShell
      title="Notifications"
      description="Configure recipients, channels, and webhook subscriptions for outbound notifications. SMTP-driven block emails live under Recipients; chat-app destinations (Teams etc.) under Channels; raw HTTP / Slack subscriptions under Webhooks."
    >
      <div
        role="tablist"
        className="flex flex-wrap items-center gap-2 p-1 rounded-lg bg-bg-inset/40 border border-border w-fit"
      >
        {TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(t.key)}
              className={
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ' +
                (isActive
                  ? 'bg-nebula/15 text-nebula border border-nebula/40 shadow-sm'
                  : 'text-fg-muted border border-transparent hover:text-fg hover:bg-bg-hover/60')
              }
            >
              <t.Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {active === 'recipients' && <AdminNotificationsBody />}
      {active === 'channels' && <AdminNotificationChannelsBody />}
      {active === 'webhooks' && <AdminWebhooksBody />}
    </AdminShell>
  );
}
